import {
    BarbColorType,
    BarbSize,
    FaaDelayMinimum,
    GDFMetarTafFlightCategory,
    HeatmapStyle,
    IataNullReportColor,
    IataTurbulenceDisplayType,
    IataTurbulencePalette,
    IataTurbValue,
    MetarDisplayMode,
    MinimumAshCloudConcentration,
    MinimumCeiling,
    MinimumContrailsLevel,
    MinimumDsua,
    MinimumIcing,
    MinimumPirepIcing,
    MinimumPirepTurbulence,
    MinimumTapsLevel,
    MinimumVisibility,
    ParticleColor,
    ProbSeverePrimaryDisplay,
    ProbSevereSecondaryDisplay,
    RadarSummaryArrowStyle,
    RvrMinimum,
    WeatherJetStreamWidth,
    WeatherLightningColor,
    WeatherLightningSymbol,
    WeatherTimelineRange,
    WeatherTimelineSyncMode,
    WeatherTimelineTimeMode,
    WindDepiction,
    WindTemp
} from '@fe/weather';
import { AlertSoundType, FeedbackType, LatLongDisplayFormatLogFileEnum } from '../../../common/enums';
import { RecordingStartFailErrors } from '../../../services/actor-messages';
import {
    DiversionManagerAirlineEnum,
    DiversionManagerCapacityFieldEnum,
    DiversionManagerNetworkEnum,
    DiversionManagerOtherEnum,
    DiversionPriorityTypeEnum
} from '../../diversion/types';

export type DropdownItem<T> = { value: T; text: string };
export type DropdownItems<T> = DropdownItem<T>[];

export const WindDepictionOptions: DropdownItems<WindDepiction> = [
    { text: 'None', value: WindDepiction.none },
    { text: 'Barbs', value: WindDepiction.barbs },
    { text: 'Particles', value: WindDepiction.particles }
];

export const WindHeatmapOptions: DropdownItems<HeatmapStyle> = [
    { text: 'None', value: HeatmapStyle.none },
    { text: 'Winds', value: HeatmapStyle.winds },
    { text: 'Temperature', value: HeatmapStyle.temperature }
];

export const WindParticleColorOptions: DropdownItems<ParticleColor> = [
    { text: 'Auto', value: ParticleColor.auto },
    { text: 'Light', value: ParticleColor.light },
    { text: 'Dark', value: ParticleColor.dark }
];

export const BarbColorTypeOptions: DropdownItems<BarbColorType> = [
    { text: 'Auto', value: BarbColorType.auto },
    { text: 'Fixed', value: BarbColorType.fixed },
    { text: 'Intensity', value: BarbColorType.intensity }
];

export const JetStreamWidthOptions: DropdownItems<WeatherJetStreamWidth> = [
    { text: 'Extra thin', value: WeatherJetStreamWidth.extraThin },
    { text: 'Thin', value: WeatherJetStreamWidth.thin },
    { text: 'Medium', value: WeatherJetStreamWidth.medium },
    { text: 'Wide', value: WeatherJetStreamWidth.wide }
];

export const WindTempOptions: DropdownItems<WindTemp> = [
    { text: 'Winds/Temperature', value: WindTemp.both },
    { text: 'Winds only', value: WindTemp.windOnly },
    { text: 'Temperature only', value: WindTemp.temperatureOnly }
];

export const BarbSizeOptions: DropdownItems<BarbSize> = [
    { text: 'Small', value: BarbSize.small },
    { text: 'Medium', value: BarbSize.medium },
    { text: 'Large', value: BarbSize.large }
];

export const MetarDisplayModeOptions: DropdownItems<MetarDisplayMode> = [
    { value: MetarDisplayMode.category, text: 'Flight category icon only' },
    { value: MetarDisplayMode.categorySky, text: 'Flight category & sky (METAR only)' },
    { value: MetarDisplayMode.categorySkyWind, text: 'Flight category, sky & wind (METAR only)' },
    { value: MetarDisplayMode.categorySkyWindValues, text: 'Flight category, sky, wind & values (METAR only)' },
    { value: MetarDisplayMode.ceiling, text: 'Ceiling icon only' },
    { value: MetarDisplayMode.visibility, text: 'Visibility icon only' }
];

export const FlightCategoryOptions: DropdownItems<GDFMetarTafFlightCategory> = [
    { value: GDFMetarTafFlightCategory.unknown, text: 'Unknown' },
    { value: GDFMetarTafFlightCategory.vfr, text: 'VFR' },
    { value: GDFMetarTafFlightCategory.mvfr, text: 'MVFR' },
    { value: GDFMetarTafFlightCategory.ifr, text: 'IFR' },
    { value: GDFMetarTafFlightCategory.lifr, text: 'LIFR' },
    { value: GDFMetarTafFlightCategory.bcat1, text: 'BCAT1' }
];

export const MinimumCeilingOptions: DropdownItems<MinimumCeiling> = [
    { value: MinimumCeiling.unknown, text: 'Unknown' },
    { value: MinimumCeiling.less200, text: '< 200 ft' },
    { value: MinimumCeiling.less500, text: '< 500 ft' },
    { value: MinimumCeiling.less1000, text: '< 1,000 ft' },
    { value: MinimumCeiling.less2000, text: '< 2,000 ft' },
    { value: MinimumCeiling.less3000, text: '< 3,000 ft' },
    { value: MinimumCeiling.less5000, text: '< 5,000 ft' },
    { value: MinimumCeiling.less10000, text: '< 10,000 ft' },
    { value: MinimumCeiling.more10000, text: '>= 10,000 ft' }
];

export const MinimumVisibilityOptions: DropdownItems<MinimumVisibility> = [
    { value: MinimumVisibility.unknown, text: 'Unknown' },
    { value: MinimumVisibility.lessQuarter, text: '< 1/4 miles' },
    { value: MinimumVisibility.lessHalf, text: '< 1/2 miles' },
    { value: MinimumVisibility.less1, text: '< 1 miles' },
    { value: MinimumVisibility.less2, text: '< 2 miles' },
    { value: MinimumVisibility.less3, text: '< 3 miles' },
    { value: MinimumVisibility.less5, text: '< 5 miles' },
    { value: MinimumVisibility.less10, text: '< 10 miles' },
    { value: MinimumVisibility.more10, text: '>= 10 miles' }
];

export const MinimumFaaDelayOptions: DropdownItems<FaaDelayMinimum> = [
    { value: FaaDelayMinimum.noDelays, text: 'No delays' },
    { value: FaaDelayMinimum.arrDep16to45, text: 'Arr/dep del 16-45 min' },
    { value: FaaDelayMinimum.arrDepMore45, text: 'Arr/dep del > 45 min' },
    { value: FaaDelayMinimum.groundDelayStop, text: 'Ground delay/stop' },
    { value: FaaDelayMinimum.closure, text: 'Airport closure' }
];

export const MinimumRvrOptions: DropdownItems<RvrMinimum> = [
    { value: RvrMinimum.all, text: 'All reporting' },
    { value: RvrMinimum.less6000, text: '< 6,000 ft' },
    { value: RvrMinimum.less2400, text: '< 2,400 ft' },
    { value: RvrMinimum.less1200, text: '< 1,200 ft' },
    { value: RvrMinimum.less600, text: '< 600 ft' },
    { value: RvrMinimum.less300, text: '< 300 ft' }
];

export const MinimumPirepTurbulenceOptions: DropdownItems<MinimumPirepTurbulence> = [
    { value: MinimumPirepTurbulence.smooth, text: 'Smooth' },
    { value: MinimumPirepTurbulence.light, text: 'Light' },
    { value: MinimumPirepTurbulence.moderate, text: 'Moderate' },
    { value: MinimumPirepTurbulence.severe, text: 'Severe' },
    { value: MinimumPirepTurbulence.extreme, text: 'Extreme' }
];

export const MinimumPirepIcingOptions: DropdownItems<MinimumPirepIcing> = [
    { value: MinimumPirepIcing.negative, text: 'Negative' },
    { value: MinimumPirepIcing.trace, text: 'Trace' },
    { value: MinimumPirepIcing.light, text: 'Light' },
    { value: MinimumPirepIcing.moderate, text: 'Moderate' },
    { value: MinimumPirepIcing.severe, text: 'Severe' }
];

export const MinimumDsuaOptions: DropdownItems<MinimumDsua> = [
    { value: MinimumDsua.notScheduled, text: 'Not Scheduled' },
    { value: MinimumDsua.h12_24, text: '12 - 24 hrs' },
    { value: MinimumDsua.h8_12, text: '8 - 12 hrs' },
    { value: MinimumDsua.h4_8, text: '4 - 8 hrs' },
    { value: MinimumDsua.h2_4, text: '2 - 4 hrs' },
    { value: MinimumDsua.h1_2, text: '1 - 2 hrs' },
    { value: MinimumDsua.h_1, text: '< 1 hr' },
    { value: MinimumDsua.active, text: 'Active' }
];

export const MinimumLevelNexrad: DropdownItems<number> = [
    { value: 0, text: '5 to < 10 dBz' },
    { value: 1, text: '10 to < 15 dBz' },
    { value: 2, text: '15 to < 20 dBz' },
    { value: 3, text: '20 to < 25 dBz' },
    { value: 4, text: '25 to < 30 dBz' },
    { value: 5, text: '30 to < 35 dBz' },
    { value: 6, text: '35 to < 40 dBz' },
    { value: 7, text: '40 to < 45 dBz' },
    { value: 8, text: '45 to < 50 dBz' },
    { value: 9, text: '50 to < 55 dBz' },
    { value: 10, text: '50 to < 60 dBz' },
    { value: 11, text: '60 to < 65 dBz' },
    { value: 12, text: '65 to < 70 dBz ' },
    { value: 13, text: '70 to < 75 dBz ' },
    { value: 14, text: '>= 75 dBz' }
];

export const MinimumRainfallNexrad: DropdownItems<number> = [
    { value: 0, text: '< 0.10 inches' },
    { value: 1, text: '0.10 to < 0.25 inches' },
    { value: 2, text: '0.25 to < 0.50 inches' },
    { value: 3, text: '0.50 to < 0.75 inches' },
    { value: 4, text: '0.75 to < 1.00 inches' },
    { value: 5, text: '1.00 to < 1.50 inches' },
    { value: 6, text: '1.50 to < 2.00 inches' },
    { value: 7, text: '2.00 to < 3.00 inches' },
    { value: 8, text: '3.00 to < 4.00 inches' },
    { value: 9, text: '4.00 to < 5.00 inches' },
    { value: 10, text: '5.00 to < 6.00 inches' },
    { value: 11, text: '6.00 to < 8.00 inches' },
    { value: 12, text: '8.00 to < 10.00 inches' },
    { value: 13, text: '10.00 to < 12.00 inches' },
    { value: 14, text: '12.00 to < 15.00 inches' },
    { value: 15, text: '>= 15.00 inches' }
];

export const MinimumEchoTopsNexrad: DropdownItems<number> = [
    { value: 0, text: '< 5,000 ft' },
    { value: 1, text: '5,000 to < 10,000 ft' },
    { value: 2, text: '10,000 to < 15,000 ft' },
    { value: 3, text: '15,000 to < 20,000 ft' },
    { value: 4, text: '20,000 to < 25,000 ft' },
    { value: 5, text: '25,000 to < 30,000 ft' },
    { value: 6, text: '30,000 to < 35,000 ft' },
    { value: 7, text: '35,000 to < 40,000 ft' },
    { value: 8, text: '40,000 to < 45,000 ft' },
    { value: 9, text: '45,000 to < 50,000 ft' },
    { value: 10, text: '50,000 to < 55,000 ft' },
    { value: 11, text: '55,000 to < 60,000 ft' },
    { value: 12, text: '60,000 to < 65,000 ft' },
    { value: 13, text: '65,000 to < 70,000 ft' },
    { value: 14, text: '>= 70,000 ft' }
];

export const MinimumLevelSatellite: DropdownItems<number> = [
    { value: 0, text: '0,000 ft' },
    { value: 2, text: '2,000 ft' },
    { value: 4, text: '4,000 ft' },
    { value: 6, text: '6,000 ft' },
    { value: 8, text: '8,000 ft' },
    { value: 10, text: '10,000 ft' },
    { value: 12, text: '12,000 ft' },
    { value: 14, text: '14,000 ft' },
    { value: 16, text: '16,000 ft' },
    { value: 18, text: '18,000 ft' },
    { value: 20, text: '20,000 ft' },
    { value: 22, text: '22,000 ft' },
    { value: 24, text: '24,000 ft' },
    { value: 26, text: '26,000 ft' },
    { value: 28, text: '28,000 ft' },
    { value: 30, text: '30,000 ft' },
    { value: 32, text: '32,000 ft' },
    { value: 34, text: '34,000 ft' },
    { value: 36, text: '36,000 ft' },
    { value: 40, text: '40,000 ft' },
    { value: 42, text: '42,000 ft' },
    { value: 44, text: '44,000 ft' },
    { value: 46, text: '46,000 ft' },
    { value: 48, text: '48,000 ft' },
    { value: 50, text: '50,000 ft' },
    { value: 52, text: '52,000 ft' },
    { value: 55, text: '55,000 ft' },
    { value: 59, text: '59,000 ft' }
];

export const MinimumTempSatellite: DropdownItems<number> = [
    { value: 30, text: '30°C' },
    { value: 20, text: '20°C' },
    { value: 10, text: '10°C' },
    { value: 0, text: '0°C' },
    { value: -10, text: '-10°C' },
    { value: -20, text: '-20°C' },
    { value: -30, text: '-30°C' },
    { value: -40, text: '-40°C' },
    { value: -50, text: '-50°C' },
    { value: -60, text: '-60°C' },
    { value: -70, text: '-70°C' },
    { value: -80, text: '-80°C' }
];

export const MinimumIataTurbLevelOptions: DropdownItems<number> = [
    { value: 0, text: '> 0.0 to < 0.1' },
    { value: 1, text: '0.1 to < 0.2' },
    { value: 2, text: '0.2 to < 0.3' },
    { value: 3, text: '0.3 to < 0.4' },
    { value: 4, text: '0.4 to < 0.5' },
    { value: 5, text: '0.5 to < 0.6' },
    { value: 6, text: '0.6 to < 0.7' },
    { value: 7, text: '0.7 to < 0.8' },
    { value: 8, text: '0.8 to < 0.9' },
    { value: 9, text: '0.9 to 1.0' }
];

export const IataTurbValueOptions: DropdownItems<IataTurbValue> = [
    { value: IataTurbValue.peak, text: 'Peak EDR' },
    { value: IataTurbValue.mean, text: 'Mean EDR' }
];

export const EdrPointsColorOptions: DropdownItems<IataNullReportColor> = [
    { value: IataNullReportColor.gray, text: 'Gray' },
    { value: IataNullReportColor.white, text: 'White' }
];

export const IataTurbDisplayTypeOptions: DropdownItems<IataTurbulenceDisplayType> = [
    { value: IataTurbulenceDisplayType.edr, text: 'EDR' },
    { value: IataTurbulenceDisplayType.light, text: 'Severity - Light aircraft' },
    { value: IataTurbulenceDisplayType.medium, text: 'Severity - Medium aircraft' },
    { value: IataTurbulenceDisplayType.heavy, text: 'Severity - Heavy aircraft' }
];

export const SeverityColorPaletteOptions: DropdownItems<IataTurbulencePalette> = [
    { value: IataTurbulencePalette.industry, text: 'Industry standard (match all other FE turbulence products)' },
    { value: IataTurbulencePalette.iata, text: 'IATA color scheme' }
];

export const MinimumTapsLevelOptions: DropdownItems<MinimumTapsLevel> = [
    { value: MinimumTapsLevel.smooth, text: 'Smooth' },
    { value: MinimumTapsLevel.rideQuality, text: 'Ride Quality' },
    { value: MinimumTapsLevel.light, text: 'Light' },
    { value: MinimumTapsLevel.moderate, text: 'Moderate' },
    { value: MinimumTapsLevel.severe, text: 'Severe' }
];

export const MinimumEdrNowcastOptions: DropdownItems<number> = [
    { value: 0, text: '> 0.0 to < 0.1' },
    { value: 1, text: '0.1 to < 0.2' },
    { value: 2, text: '0.2 to < 0.3' },
    { value: 3, text: '0.3 to < 0.4' },
    { value: 4, text: '0.4 to < 0.5' },
    { value: 5, text: '0.5 to < 0.6' },
    { value: 6, text: '0.6 to < 0.7' },
    { value: 7, text: '0.7 to < 0.8' },
    { value: 8, text: '0.8 to < 0.9' },
    { value: 9, text: '0.9 to 1.0' }
];

export const MinimumIcingOptions: DropdownItems<MinimumIcing> = [
    { value: MinimumIcing.trace, text: 'Trace' },
    { value: MinimumIcing.light, text: 'Light' },
    { value: MinimumIcing.moderate, text: 'Moderate' },
    { value: MinimumIcing.heavy, text: 'Heavy' }
];

export const AnimationTimeToShowEachSlide: DropdownItems<number> = [
    { value: 0.25, text: '1/4 seconds' },
    { value: 0.5, text: '1/2 seconds' },
    { value: 1, text: '1 seconds' },
    { value: 2, text: '2 seconds' },
    { value: 5, text: '5 seconds' },
    { value: 10, text: '10 seconds' }
];

export const AnimationTimePauseBetweenCycles: DropdownItems<number> = [
    { value: 0, text: '0 (no pause)' },
    { value: 1, text: '1 seconds' },
    { value: 2, text: '2 seconds' },
    { value: 5, text: '5 seconds' },
    { value: 10, text: '10 seconds' },
    { value: 20, text: '20 seconds' },
    { value: 30, text: '30 seconds' }
];

export const TimelineSyncMode: DropdownItems<WeatherTimelineSyncMode> = [
    { value: WeatherTimelineSyncMode.auto, text: 'Auto' },
    { value: WeatherTimelineSyncMode.mostFrequent, text: 'Most frequent' },
    { value: WeatherTimelineSyncMode.topMost, text: 'Top most' },
    { value: WeatherTimelineSyncMode.bottomMost, text: 'Bottom most' },
    { value: WeatherTimelineSyncMode.custom, text: 'Custom' }
];

export const TimelineTimeRange: DropdownItems<WeatherTimelineRange> = [
    { value: WeatherTimelineRange.auto, text: 'Auto' },
    { value: WeatherTimelineRange.historyShort, text: 'History short (-2 to + 2)' },
    { value: WeatherTimelineRange.historyMid, text: 'History mid (-4 to + 2)' },
    { value: WeatherTimelineRange.historyLong, text: 'History long (-6 to + 2)' },
    { value: WeatherTimelineRange.forecastShort, text: 'Forecast short (-2 to +6)' },
    { value: WeatherTimelineRange.forecastMid, text: 'Forecast mid (-2 to + 12)' },
    { value: WeatherTimelineRange.forecastLong, text: 'Forecast long (-2 to +24)' },
    { value: WeatherTimelineRange.full, text: 'Full (-6 to +24)' }
];

export const TimelineTimeMode: DropdownItems<WeatherTimelineTimeMode> = [
    { value: WeatherTimelineTimeMode.relative, text: 'Relative' },
    { value: WeatherTimelineTimeMode.absolute, text: 'Absolute' }
];

export const MinimumContrailsLevelOptions: DropdownItems<MinimumContrailsLevel> = [
    { value: MinimumContrailsLevel.low, text: 'Low' },
    { value: MinimumContrailsLevel.moderate, text: 'Moderate' },
    { value: MinimumContrailsLevel.high, text: 'High' },
    { value: MinimumContrailsLevel.extreme, text: 'Extreme' }
];

export const RadarSummaryArrowStyleOptions: DropdownItems<RadarSummaryArrowStyle> = [
    { value: RadarSummaryArrowStyle.fixed, text: 'Fixed length arrow' },
    { value: RadarSummaryArrowStyle.speedBased, text: 'Speed based arrow' }
];

export const LightningSymbolOptions: DropdownItems<WeatherLightningSymbol> = [
    { value: WeatherLightningSymbol.dot, text: 'Dot' },
    { value: WeatherLightningSymbol.plus, text: 'Plus' },
    { value: WeatherLightningSymbol.sign, text: '+/-' },
    { value: WeatherLightningSymbol.icon, text: 'Icon' }
];

export const LightingColorOptions: DropdownItems<WeatherLightningColor> = [
    { value: WeatherLightningColor.faded, text: 'Faded' },
    { value: WeatherLightningColor.discrete, text: 'Discrete' }
];

export const MinimumThunderStormLevelOptions: DropdownItems<number> = [
    { value: 0, text: '5 m/s' },
    { value: 1, text: '10 m/s' },
    { value: 2, text: '15 m/s' },
    { value: 3, text: '20 m/s' },
    { value: 4, text: '25 m/s' },
    { value: 5, text: '30 m/s' },
    { value: 6, text: '35 m/s' },
    { value: 7, text: '40 m/s' },
    { value: 8, text: '45 m/s' },
    { value: 9, text: '50+ m/s' }
];

export const ProbSeverePrimaryDisplayOptions: DropdownItems<ProbSeverePrimaryDisplay> = [
    { value: ProbSeverePrimaryDisplay.combined, text: 'Combined' },
    { value: ProbSeverePrimaryDisplay.wind, text: 'Wind only' },
    { value: ProbSeverePrimaryDisplay.hail, text: 'Hail only' },
    { value: ProbSeverePrimaryDisplay.tornado, text: 'Tornado only' }
];

export const ProbSevereSecondaryDisplayOptions: DropdownItems<ProbSevereSecondaryDisplay> = [
    { value: ProbSevereSecondaryDisplay.none, text: 'None' },
    { value: ProbSevereSecondaryDisplay.wind, text: 'Wind' },
    { value: ProbSevereSecondaryDisplay.hail, text: 'Hail' },
    { value: ProbSevereSecondaryDisplay.tornado, text: 'Tornado' }
];

export const ProbSeverMinimumLevelOptions: DropdownItems<number> = [
    { value: 0, text: '0%' },
    { value: 30, text: '30%' },
    { value: 50, text: '50%' },
    { value: 70, text: '70%' },
    { value: 90, text: '90%' }
];

export const FeedbackTypeOptions: DropdownItems<FeedbackType> = [
    { value: FeedbackType.none, text: 'None' },
    { value: FeedbackType.generalComment, text: 'General comment' },
    { value: FeedbackType.featureRequest, text: 'Feature request' },
    { value: FeedbackType.technicalQuestion, text: 'Technical question' },
    { value: FeedbackType.troubleReport, text: 'Trouble report' }
];

export const DiversionManagerAirlineOptions: DropdownItems<DiversionManagerAirlineEnum> = [
    { value: DiversionManagerAirlineEnum.airline, text: 'Airline' },
    { value: DiversionManagerAirlineEnum.major, text: 'Major' }
];

export const DiversionManagerNetworkOptions: DropdownItems<DiversionManagerNetworkEnum> = [
    { value: DiversionManagerNetworkEnum.network, text: 'Network' },
    { value: DiversionManagerNetworkEnum.subCarrier, text: 'Sub-carrier' }
];

export const DiversionManagerOtherOptions: DropdownItems<DiversionManagerOtherEnum> = [
    { value: DiversionManagerOtherEnum.other, text: 'Other' },
    { value: DiversionManagerOtherEnum.oal, text: 'OAL' }
];

export const DiversionManagerCapacityFieldOptions: DropdownItems<DiversionManagerCapacityFieldEnum> = [
    { value: DiversionManagerCapacityFieldEnum.total, text: 'Total' },
    { value: DiversionManagerCapacityFieldEnum.narrow, text: 'Narrow' },
    { value: DiversionManagerCapacityFieldEnum.wideNarrow, text: 'Narrow/Wide' }
];

export const DiversionPriorityTypeOptions: DropdownItems<DiversionPriorityTypeEnum> = [
    { value: DiversionPriorityTypeEnum.faa, text: 'FAA' },
    { value: DiversionPriorityTypeEnum.custom, text: 'Custom' }
];

export const LatLongDisplayFormatLogFileOptions: DropdownItems<LatLongDisplayFormatLogFileEnum> = [
    { value: LatLongDisplayFormatLogFileEnum.Default, text: 'Default (user global setting)' },
    { value: LatLongDisplayFormatLogFileEnum.SignedDecimalDegrees, text: 'Signed decimal degrees (23.4567, -123.4567)' },
    { value: LatLongDisplayFormatLogFileEnum.UnsignedDecimalDegrees, text: 'Decimal degrees (23.4567N, 123.4567W)' },
    { value: LatLongDisplayFormatLogFileEnum.DegreesAndDecimalMinutes, text: 'Degrees and decimal minutes (N23 27.402, W123 27.402)' },
    {
        value: LatLongDisplayFormatLogFileEnum.DegreesMinutesAndDecimalSeconds,
        text: 'Degrees, minutes and decimal seconds (23 27 24.12N, 123 27 24.12W)'
    }
];

export const AlertSoundTypeOptions: DropdownItems<AlertSoundType> = [
    { value: AlertSoundType.sound1, text: 'Sound 1' },
    { value: AlertSoundType.sound2, text: 'Sound 2' },
    { value: AlertSoundType.sound3, text: 'Sound 3' }
];

export const MinimumAshCloudConcentrationOptions: DropdownItems<MinimumAshCloudConcentration> = [
    { value: MinimumAshCloudConcentration.low, text: 'Low 0.2-2.0 mg/m3' },
    { value: MinimumAshCloudConcentration.medium, text: 'Medium 2.0-5.0 mg/m3' },
    { value: MinimumAshCloudConcentration.high, text: 'High 5.0-10.0 mg/m3' },
    { value: MinimumAshCloudConcentration.veryHigh, text: 'Very high >= 10.0 mg/m3' }
];

export const RecordingStartFailText: Record<RecordingStartFailErrors, string> = {
    [RecordingStartFailErrors.noDir]: 'No recording directory set.',
    [RecordingStartFailErrors.alreadyExists]: 'File already exists.',
    [RecordingStartFailErrors.alreadyRecording]: 'Recording already in progress.',
    [RecordingStartFailErrors.notSupported]: 'Recording is not support in this browser. Please use Chromium-based browser.',
    [RecordingStartFailErrors.unknown]: "Can't start recording, unknown error."
};
